// game/shared/src/obstacles.ts
//
// Shared static-obstacle model: the collision radius of every solid decorative
// doodad + canyon rock/wall, and a uniform-grid field that ejects a unit out of
// them. SHARED so the authoritative server (HelloRoom pushout + navmesh) and the
// client's local-hero PREDICTION use the IDENTICAL geometry — otherwise the
// predicted hero walks through rocks the server treats as solid (the rendered
// mesh sinks into a boulder while the server holds the true position at its rim).
//
// The server reads the world files (objects.json / regions.json) and wraps these
// in fs loaders (static-obstacles.ts); the client builds the same list from the
// data it already fetched. Both then build the same field.

import { DOODAD_REGISTRY, resolveDoodad } from "./doodad-registry";
import { MESH_RADIUS } from "./doodad-placement";
import { generateCanyonPlacements, type CanyonRegion } from "./canyon-placement";

export interface StaticObstacle { x: number; z: number; r: number }

// Horizontal offset (local X,Z) of a mesh's geometric centre from its pivot, for
// meshes whose geometry is NOT centred on the origin. The client instances raw
// geometry at the placement point, so an offset mesh renders shifted from that
// point — and a collider circle placed at the pivot would miss the part of the
// rock that juts past it (you can "skirt the edge and walk into part of it").
// Recentre ROCK / CANYON colliders by this (rotated + scaled); TREES keep their
// collider at the pivot (the bbox centre is the canopy, the trunk is at origin).
// Generated from the GLB raw bounds (tools/asset-export); only entries that
// matter (offset > ~0.15u) are listed — everything else is treated as [0,0].
const MESH_CENTER: Record<string, readonly [number, number]> = {
  exRockL1A: [0.62, -0.56], exRockL1B: [0.48, -0.76], exRockL2A: [0.05, -0.42], exRockL3A: [-0.04, 0.54],
  exRockS1C: [0.03, 0.16], exRockS1D: [0.2, 0.18],
  exCanyonW1A: [-0.12, -5.13], exCanyonW2A: [0.59, -5.3], exCanyonW3A: [0.03, -4.97],
  exCliff1A: [0.3, 0.03], exCliff2A: [0.05, 0.21], exCliff5A: [0.81, 0.37],
};

// Apply a mesh's centre offset (local X,Z scaled by `s`, rotated by `rot`) to a
// placement point, so the collider circle sits on the rendered mesh, not its pivot.
function recentre(px: number, pz: number, meshKey: string, s: number, rot: number): { x: number; z: number } {
  const c = MESH_CENTER[meshKey];
  if (!c) return { x: px, z: pz };
  const dx = c[0] * s, dz = c[1] * s;
  const cos = Math.cos(rot), sin = Math.sin(rot);
  return { x: px + dx * cos + dz * sin, z: pz - dx * sin + dz * cos };
}

// Fallback render scale for doodad types not in DOODAD_REGISTRY (mirrors the
// client's FALLBACK_BUCKETS in world/doodads.ts).
const FALLBACK_RENDER_SCALE: Record<"tree" | "rock" | "other", number> = {
  tree: 1.14, rock: 3.0, other: 1.09,
};

// Rock collider as a fraction of the mesh's widest horizontal radius. =1 so the
// circle matches the visible footprint: a unit is ejected to `collider + unitR`
// from the rock centre, leaving its BODY edge at the rock face, not sunk inside.
export const ROCK_COLLIDER_FACTOR = 1.0;
// Canyon interior rocks collide like decorative rocks; rim walls/cliffs don't
// collide at all (see canyonObstacleRadius).
const CANYON_ROCK_FACTOR = ROCK_COLLIDER_FACTOR;
// Headroom over the largest obstacle radius when sizing the grid cell, covering
// the biggest querying unit's body radius (players 0.6, creatures up to ~2). The
// 3×3 neighbourhood only sees obstacles whose CENTRE is within one cell, so cell
// MUST be ≥ maxObstacleR + maxUnitR or a unit overlapping a big obstacle slips
// through (the "walk through big rocks" bug).
const MAX_QUERY_UNIT_RADIUS = 3;

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export function registryRenderScale(type: string): number {
  const row = DOODAD_REGISTRY[type];
  if (row && row !== "skip") return row.scale;
  if (/Tree$/i.test(type)) return FALLBACK_RENDER_SCALE.tree;
  if (/Rock|Rubble|Stone/i.test(type)) return FALLBACK_RENDER_SCALE.rock;
  return FALLBACK_RENDER_SCALE.other;
}

/**
 * Collision radius (world units) of a decorative doodad, or null if walk-through.
 * Rocks track the VISIBLE mesh when `variant` (the resolved NatureKey the client
 * renders) is known; trees stay a modest trunk so units weave between them (this
 * radius also feeds the navmesh carve — over-inflating trunks re-seals gaps).
 */
export function doodadObstacleRadius(type: string, scale: number, variant?: string): number | null {
  const objScale = scale > 0 ? scale : 1;
  const s = objScale * registryRenderScale(type);
  if (/Tree$/i.test(type)) return clamp(0.18 * s, 0.4, 0.9);
  if (/Rock|Rubble|Stone/i.test(type)) {
    if (variant !== undefined && MESH_RADIUS[variant] !== undefined) {
      // Half-widest-axis radius (recentred on the mesh + rotated to match the
      // render). NOT the full bounding circle: with the centre/rotation fixed,
      // half-axis already covers every observed walk-in, and the larger bounding
      // circle over-blocked real traversal corridors (sealed the Shakuras rock
      // field — qa-navmesh "near mammoth → north"). Residual: ~0.4u of a rock's
      // diagonal corner can clip; revisit in the pathfinding workstream.
      const visR = MESH_RADIUS[variant] * registryRenderScale(type) * objScale;
      return clamp(visR * ROCK_COLLIDER_FACTOR, 0.5, 5.5);
    }
    return clamp(0.85 * s, 0.6, 2.5); // unregistered/miss rock: render-scale estimate
  }
  return null;
}

/** Canyon placement collider radius, or null if walk-through.
 *  RIM WALLS/CLIFFS are intentionally NON-colliding: they ring the large dry
 *  "Fish" basins, and a solid (necessarily big) circular collider on each seals
 *  the basin off — making a 45×45u interior unreachable (caught by qa-navmesh:
 *  "near mammoth → north" couldn't cross the Shakuras basin rim). The basins are
 *  playable terrain, so the rim stays passable; only the INTERIOR rock clusters
 *  are solid (you don't walk through basin boulders). Revisit in the pathfinding
 *  workstream if walls should seal as a deliberate design choice (see QUEUE.md). */
export function canyonObstacleRadius(meshKey: string, scale: number, kind: "wall" | "rock"): number | null {
  if (kind === "wall") return null;
  const meshR = MESH_RADIUS[meshKey];
  if (meshR === undefined) return null;
  return clamp(meshR * scale * CANYON_ROCK_FACTOR, 0.6, 5.5);
}

// Row id mirrors client world/doodads.ts rowId — keep in sync so the variant
// resolved for collision matches the one rendered (and thus the mesh we collide).
export function doodadRowId(o: { type: string; x: number; y: number; z: number; rot?: number }): string {
  return `${o.type}@${o.x.toFixed(2)},${o.y.toFixed(2)},${o.z.toFixed(2)},${(o.rot ?? 0).toFixed(2)}`;
}

export type CollectRow = {
  kind: "unit" | "doodad";
  type: string;
  x: number;
  y: number; // world-Z plane (objects.json convention)
  z: number;
  rot?: number;
  scale?: number;
};

/** Obstacle for one resolved doodad row, or null if walk-through. Rocks are
 *  recentred on their visible mesh; trees keep their trunk collider at the pivot. */
export function doodadObstacle(o: CollectRow, variant?: string): StaticObstacle | null {
  const r = doodadObstacleRadius(o.type, o.scale ?? 1, variant);
  if (r === null) return null;
  if (!/Rock|Rubble|Stone/i.test(o.type) || !variant) return { x: o.x, z: o.y, r };
  const s = registryRenderScale(o.type) * (o.scale && o.scale > 0 ? o.scale : 1);
  // Match the render's mesh rotation EXACTLY (world/doodads.ts: rotation.y =
  // -o.rot + entry.rotOffset) so the recentred collider lands on the drawn rock.
  // Using +o.rot left rotated rocks' colliders mirrored off the visible mesh.
  const row = DOODAD_REGISTRY[o.type];
  const rotOffset = row && row !== "skip" ? row.rotOffset ?? 0 : 0;
  const c = recentre(o.x, o.y, variant, s, -(o.rot ?? 0) + rotOffset);
  return { x: c.x, z: c.z, r };
}

/** Obstacle for one canyon placement, recentred on its visible mesh. */
export function canyonObstacle(p: { meshKey: string; kind: "wall" | "rock"; x: number; z: number; scale: number; rotY: number }): StaticObstacle | null {
  const r = canyonObstacleRadius(p.meshKey, p.scale, p.kind);
  if (r === null) return null;
  const c = recentre(p.x, p.z, p.meshKey, p.scale, p.rotY);
  return { x: c.x, z: c.z, r };
}

// Bespoke scenery landmarks that are solid but live in no data file (client
// scenery meshes, not objects.json rows). SINGLE source of truth for both the
// visual anchor (client world.ts imports VOLCANO_LANDMARK for placement) and
// the collision circle — the volcano previously kept its own hardcoded coords
// in world.ts, which is exactly how it got stranded at pre-flip coordinates
// (2026-07-06 pink-surface investigation). x/z = post-flip mammoth-lair centre.
export const VOLCANO_LANDMARK = { x: 77, z: 214.5, r: 6.5 };
export const LANDMARK_OBSTACLES: StaticObstacle[] = [
  { x: VOLCANO_LANDMARK.x, z: VOLCANO_LANDMARK.z, r: VOLCANO_LANDMARK.r },
];

/**
 * Build the full static-obstacle list (decorative doodads + canyon framing +
 * bespoke landmarks) from the ALREADY-PLACEMENT-RESOLVED doodad rows + regions.
 * Same output the server field/navmesh use, so the client prediction can eject
 * the hero identically. (Harvestable resource nodes are handled separately —
 * they live in synced state.)
 */
export function collectStaticObstacles(resolvedRows: CollectRow[], regions: CanyonRegion[]): StaticObstacle[] {
  const out: StaticObstacle[] = [];
  for (const o of resolvedRows) {
    if (o.kind !== "doodad") continue;
    const res = resolveDoodad(o.type, doodadRowId(o));
    const variant = res.kind === "mesh" ? res.variant : undefined;
    const ob = doodadObstacle(o, variant);
    if (ob) out.push(ob);
  }
  for (const p of generateCanyonPlacements(regions)) {
    const ob = canyonObstacle(p);
    if (ob) out.push(ob);
  }
  out.push(...LANDMARK_OBSTACLES);
  return out;
}

export interface ObstacleField {
  /** Eject a unit of `unitRadius` out of any nearby solid. Returns the corrected
      position; `pushed` is true if anything moved it. */
  resolve(px: number, pz: number, unitRadius: number): { x: number; z: number; pushed: boolean };
  readonly count: number;
}

/**
 * Bucket obstacles into a uniform grid so a pushout only tests a unit against its
 * own cell + 8 neighbours. When `cellArg` isn't given, the cell is derived from
 * the largest obstacle so the 3×3 neighbourhood can never miss an overlap
 * (cell ≥ maxObstacleR + MAX_QUERY_UNIT_RADIUS).
 */
export function buildObstacleField(obstacles: StaticObstacle[], cellArg?: number): ObstacleField {
  let maxR = 0;
  for (const o of obstacles) if (o.r > maxR) maxR = o.r;
  const cell = cellArg ?? Math.max(5, Math.ceil(maxR + MAX_QUERY_UNIT_RADIUS));
  const grid = new Map<string, StaticObstacle[]>();
  const key = (cx: number, cz: number): string => `${cx},${cz}`;
  for (const o of obstacles) {
    const k = key(Math.floor(o.x / cell), Math.floor(o.z / cell));
    let bucket = grid.get(k);
    if (!bucket) { bucket = []; grid.set(k, bucket); }
    bucket.push(o);
  }
  return {
    count: obstacles.length,
    resolve(px, pz, unitRadius) {
      let x = px, z = pz, pushed = false;
      const cx = Math.floor(x / cell), cz = Math.floor(z / cell);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          const bucket = grid.get(key(cx + dx, cz + dz));
          if (!bucket) continue;
          for (const o of bucket) {
            const ddx = x - o.x, ddz = z - o.z;
            const reach = o.r + unitRadius;
            const d2 = ddx * ddx + ddz * ddz;
            if (d2 >= reach * reach) continue;
            const d = Math.sqrt(d2);
            if (d > 1e-6) { x = o.x + (ddx / d) * reach; z = o.z + (ddz / d) * reach; }
            else { x = o.x + reach; } // exact centre: push along +x deterministically
            pushed = true;
          }
        }
      }
      return { x, z, pushed };
    },
  };
}
